import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Plus, Mail, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const TIER_LABEL = { one_pager: "1-pager", five_pager: "5-pager" };

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

export default function ConnectorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", firm: "", tier: "one_pager", send_email: true, introducer_name: "", introducer_email: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/investors");
      setInvestors(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  // Default the introducer to the connector themselves — they are the introducer.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      introducer_name: f.introducer_name || user.name || "",
      introducer_email: f.introducer_email || user.email || "",
    }));
  }, [user]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const createInvestor = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        introducer_name: form.introducer_name?.trim() || null,
        introducer_email: form.introducer_email?.trim() || null,
      };
      const { data } = await api.post("/admin/investors", payload);
      if (data.email_sent) toast.success(`Investor added — ${TIER_LABEL[data.tier]} emailed to ${data.email}`);
      else toast.error(data.email_error || `Investor added but the email could not be sent`);
      setForm({ name: "", email: "", firm: "", tier: form.tier, send_email: true,
                introducer_name: user?.name || "", introducer_email: user?.email || "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create investor");
    } finally { setCreating(false); }
  };

  const resend = async (id, email) => {
    try {
      const { data } = await api.post(`/admin/investors/${id}/invite-email`);
      if (data.email_sent) toast.success(`Resent to ${email}`);
      else toast.error(data.email_error || "Resend failed");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend"); }
  };

  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-ink/90 backdrop-blur-md px-6 py-3 lg:px-10">
        <div className="flex items-center gap-2">
          <Logo imgClass="h-8" />
          <span className="ml-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">Connector Console</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block font-mono-data text-xs text-ftext3">{user?.email}</span>
          <button data-testid="connector-refresh-btn" onClick={load} className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-sky transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button data-testid="connector-logout-btn" onClick={doLogout} className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-[#FCA5A5] transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 py-8 lg:px-10 space-y-8 max-w-5xl mx-auto">
        <div className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Introduce an investor · they will receive the chosen PDF by email
          </div>
          <form onSubmit={createInvestor} className="p-5 grid gap-3 md:grid-cols-3 md:items-end">
            <div className="space-y-1.5">
              <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Material to send</span>
              <select data-testid="connector-tier-select" value={form.tier} onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))} className="w-full bg-ink border border-line2 px-3 py-2 text-sm text-ftext focus:outline-none focus:border-sky h-[42px]">
                <option value="one_pager">1-pager · PDF attachment</option>
                <option value="five_pager">5-pager · PDF attachment</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Full name</span>
              <Input data-testid="connector-inv-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Investor" className="rounded-none bg-ink border-line2" />
            </div>
            <div className="space-y-1.5">
              <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Email</span>
              <Input data-testid="connector-inv-email-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@fund.com" className="rounded-none bg-ink border-line2" />
            </div>
            <div className="space-y-1.5">
              <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Firm (optional)</span>
              <Input data-testid="connector-inv-firm-input" value={form.firm} onChange={(e) => setForm((f) => ({ ...f, firm: e.target.value }))} placeholder="Northstar Capital" className="rounded-none bg-ink border-line2" />
            </div>
            <div className="space-y-1.5">
              <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Introduced by (optional)</span>
              <Input data-testid="connector-inv-introducer-name" value={form.introducer_name} onChange={(e) => setForm((f) => ({ ...f, introducer_name: e.target.value }))} placeholder={user?.name || "You"} className="rounded-none bg-ink border-line2" />
            </div>
            <div className="space-y-1.5">
              <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Introducer email (CC)</span>
              <Input data-testid="connector-inv-introducer-email" type="email" value={form.introducer_email} onChange={(e) => setForm((f) => ({ ...f, introducer_email: e.target.value }))} placeholder={user?.email || ""} className="rounded-none bg-ink border-line2" />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 font-mono-data text-xs text-ftext2 select-none">
              <input data-testid="connector-sendemail-checkbox" type="checkbox" checked={form.send_email} onChange={(e) => setForm((f) => ({ ...f, send_email: e.target.checked }))} className="accent-[#38BDF8]" />
              Email the PDF immediately
            </label>
            <button data-testid="connector-create-btn" type="submit" disabled={creating} className="flex items-center justify-center gap-2 bg-sky hover:bg-skyH text-primary-foreground font-mono-data text-sm font-semibold uppercase tracking-wider px-4 py-2.5 transition-colors disabled:opacity-60 h-[42px]">
              <Plus className="w-4 h-4" /> {creating ? "Sending…" : "Send"}
            </button>
          </form>
        </div>

        <div className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Your introductions ({investors.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line font-mono-data text-xs uppercase tracking-wider text-ftext3">
                  {["Investor", "Material", "Sent", "Introducer", "Actions"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="connector-investors-table">
                {investors.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-ftext3 font-mono-data text-xs">No introductions yet.</td></tr>}
                {investors.map((iv) => (
                  <tr key={iv.id} data-testid={`conn-row-${iv.email}`} className="border-b border-line/60 last:border-0">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-ftext">{iv.name}</div>
                      <div className="font-mono-data text-xs text-ftext3">{iv.email}</div>
                      {iv.firm && <div className="text-xs text-ftext2">{iv.firm}</div>}
                    </td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext2 whitespace-nowrap">{TIER_LABEL[iv.tier] || iv.tier}</td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext3 whitespace-nowrap">{fmt(iv.invite_sent_at)}</td>
                    <td className="px-5 py-3 text-xs text-ftext2 whitespace-nowrap">{iv.introducer_name || "—"}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <button data-testid={`conn-resend-${iv.email}`} onClick={() => resend(iv.id, iv.email)} className="font-mono-data text-[11px] uppercase tracking-wider border border-line2 text-ftext2 hover:text-sky hover:border-sky px-2 py-1 inline-flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> Resend
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
